import { useContext, useEffect, useMemo, useRef, useState, type FC } from 'react';
import classNames from 'classnames';
import Minimap from 'react-minimap';
import { useTranslation } from 'react-i18next';
import useScrollInfo from 'react-element-scroll-hook';

import DoubleArrowIcon from 'app/icons/arrow-double.svg';
import ConfigContext from 'app/common/ConfigContext';
import BracketMatch from 'app/components/Blocks/BracketsBlock/BracketMatch';
import { getVideogameTitle } from 'app/config/videogameSpecific.config.helper';
import { groupByPredicate, sort } from 'app/utils/array';
import { formatDate } from 'app/utils/date';
import DisplayContext from 'app/common/DisplayContext';
import InfoIcon from 'app/icons/info.svg';

import {
  getMatchLiaisons,
  getOrderedColumns,
  getPredecessorsLength,
  getSeedLiaisons,
  getSeedTitle,
  isPlaceholderMatch
} from './utils';
import style from './style.module.scss';

interface Props {
  bracket: TournamentBracketWithSeeds;
  withTabs?: boolean;
}

const RenderMinimapSeed = ({ width, height, left, top }: Record<string, number>) => (
  <div
    className={style.minimapSeed}
    style={{
      width,
      height,
      left,
      top
    }}
  />
);

// Allow to init useScrollInfo with a fake div element, before initializing it with the real which is not accessible in JSX
const FakeDivElement = document.createElement('div');
let flag = true;

const BracketWithSeeds: FC<Props> = ({ bracket, withTabs = false }) => {
  const { t } = useTranslation();
  const { full_height, lang } = useContext(ConfigContext);
  const { displayedBlocks } = useContext(DisplayContext);

  const [hoveredTeamId, setHoveredTeamId] = useState<number | null>(null);

  const baseRef = useRef<HTMLDivElement | null>(null);

  // I'd like to rewrite this plugin but for now it'll do
  const [scrollInfo, setRef] = useScrollInfo();

  if (flag) {
    setRef(FakeDivElement);
    flag = false;
  }

  useEffect(() => {
    setRef(baseRef.current?.parentElement?.parentElement);
  }, [baseRef, bracket.id]);

  useEffect(
    () => () => {
      flag = true;
      // baseRef.current = null;
    },
    []
  );

  const scrollInfoX = scrollInfo.x.percentage;
  const scrollInfoY = scrollInfo.y.percentage;

  const withMinimap = scrollInfoX !== null || scrollInfoY !== null;

  const swissStageDisplay = bracket.tournament_type === 'swiss_stage';
  const reseedingDisplay = bracket.tournament_type === 'reseeding';
  const matchDisplay = swissStageDisplay || reseedingDisplay ? 'compact' : 'default';

  const [orderedColumns, thirdPlaceSeed] = useMemo(
    () => getOrderedColumns(bracket.columns, bracket.tournament_type, bracket.id),
    [bracket.columns, bracket.tournament_type]
  );

  const maxNumberOfSeeds =
    swissStageDisplay || reseedingDisplay
      ? 1
      : Math.max(...orderedColumns.map(column => column.seeds.length));
  const numberOfColumns = orderedColumns.length;

  const numberOfColumnsDisplayed = Math.min(orderedColumns.length, 3);

  // Compute the index of the first column that contains a match for each seed
  const firstSeedColumnIndex = orderedColumns.reduce((acc, column, columnIndex) => {
    column.seeds.forEach((seed, seedIndex) => {
      if (acc[seedIndex] === undefined && seed.matches.length !== 0) {
        acc[seedIndex] = columnIndex;
      }
    });
    return acc;
  }, [] as number[]);

  const withStandingDisclaimerMessage =
    swissStageDisplay &&
    bracket.columns.every(column => column.seeds.every(seed => seed.matches.length === 0));

  const thirdPlaceSeedTitle = getSeedTitle(
    t,
    thirdPlaceSeed,
    1,
    bracket.tournament_type,
    bracket.id
  );

  return (
    <>
      <div
        className={classNames(style.upArrow, {
          [style.arrowDisplayed]: scrollInfoY !== null && scrollInfoY !== 0
        })}
      >
        <DoubleArrowIcon className={style.arrowIcon} />
      </div>
      <div
        className={classNames(style.leftArrow, {
          [style.arrowDisplayed]: scrollInfoX !== null && scrollInfoX !== 0
        })}
      >
        <DoubleArrowIcon className={style.arrowIcon} />
      </div>
      <div
        className={classNames(style.downArrow, {
          [style.arrowDisplayed]: scrollInfoY !== null && scrollInfoY !== 1
        })}
      >
        <DoubleArrowIcon className={style.arrowIcon} />
      </div>
      <div
        className={classNames(style.rightArrow, {
          [style.arrowDisplayed]: scrollInfoX !== null && scrollInfoX !== 1
        })}
      >
        <DoubleArrowIcon className={style.arrowIcon} />
      </div>
      <Minimap
        key={bracket.id}
        selector=".seed"
        childComponent={RenderMinimapSeed}
        className={classNames(style.minimapContainer, {
          [style.noMinimap]: !withMinimap,
          [style.fullHeight]: full_height && displayedBlocks.length === 1,
          [style.withTabs]: withTabs
        })}
        height={46}
        keepAspectRatio
      >
        <div
          className={style.bracket}
          style={{
            width: `${(100 / numberOfColumnsDisplayed) * numberOfColumns}%`,
            minHeight: full_height ? '100%' : `${100 * maxNumberOfSeeds}%`
          }}
          ref={baseRef}
        >
          {orderedColumns.map((column, columnIndex) => (
            // Column
            <div
              className={classNames(style.column, {
                [style.compact]: matchDisplay === 'compact',
                [style.withThirdPlaceMatch]: thirdPlaceSeed !== undefined,
                [style.withMinimap]: withMinimap
              })}
              key={columnIndex}
            >
              {column.seeds.map((seed, seedIndex) => {
                // Seed
                const prevCol: TournamentBracketColumn | undefined =
                  orderedColumns[columnIndex - 1];
                const nextCol: TournamentBracketColumn | undefined =
                  orderedColumns[columnIndex + 1];
                const isEmptyWithNoPredecessor = columnIndex < firstSeedColumnIndex[seedIndex];
                const isEmptyWithPredecessor = seed.matches.length === 0;

                const predecessorsLength = getPredecessorsLength(
                  columnIndex,
                  seedIndex,
                  orderedColumns
                );
                const playingSeed =
                  seed.position === 'lower'
                    ? column.seeds.find(s => s.position === 'upper')
                    : column.seeds.find(s => s.position === 'lower');

                const [prevLiaison, nextLiaison] = reseedingDisplay
                  ? [undefined, undefined]
                  : getSeedLiaisons(
                      swissStageDisplay
                        ? columnIndex
                        : columnIndex - firstSeedColumnIndex[seedIndex],
                      prevCol,
                      column,
                      seedIndex,
                      nextCol
                    );

                const seedTitle = getSeedTitle(
                  t,
                  seed,
                  column.seeds.length,
                  bracket.tournament_type,
                  bracket.id
                );
                const playingSeedTitle = getSeedTitle(
                  t,
                  playingSeed,
                  column.seeds.length,
                  bracket.tournament_type,
                  bracket.id
                );

                return (
                  <div
                    className={classNames(style.seed, {
                      seed: !isEmptyWithNoPredecessor && !isEmptyWithPredecessor,
                      [style.compact]: matchDisplay === 'compact',
                      [style.alignToTop]: reseedingDisplay
                    })}
                    key={seedIndex}
                  >
                    <div
                      className={classNames(
                        style.seedLiaisonContainer,
                        style.prevLiaisonContainer,
                        prevLiaison && style[prevLiaison]
                      )}
                    >
                      <div />
                    </div>
                    <div className={style.seedTitleContainer}>
                      {seed.title && <h3 className={style.seedTitle}>{seedTitle}</h3>}
                    </div>
                    <div
                      className={classNames(style.seedContent, {
                        [style.compact]: matchDisplay === 'compact',
                        [style.withDates]: reseedingDisplay
                      })}
                    >
                      {isEmptyWithNoPredecessor ? (
                        <div className={style.emptySeed}>
                          {playingSeed?.title &&
                            t('blocks_bracket_seed_empty', {
                              seed: playingSeedTitle
                            })}
                        </div>
                      ) : (
                        <>
                          {isEmptyWithPredecessor ? (
                            <>
                              {new Array(predecessorsLength).fill(true).map((_, index) => (
                                <div
                                  className={style.straightLiaisonAccross}
                                  key={`straight_liaison_${columnIndex}_${seedIndex}_${index}`}
                                />
                              ))}
                            </>
                          ) : (
                            // Match
                            <>
                              {reseedingDisplay ? (
                                <>
                                  {Object.entries(
                                    groupByPredicate(
                                      sort(seed.matches, match => match.begin_at, false),
                                      match =>
                                        match.begin_at
                                          ? formatDate(new Date(match.begin_at), lang, {
                                              month: 'long',
                                              day: '2-digit'
                                            })
                                          : 'TBD'
                                    )
                                  ).map(([date, matches], dateIndex) => (
                                    <div
                                      key={date}
                                      className={classNames(style.seedContent, style.compact)}
                                    >
                                      <div className={style.date}>{date}</div>
                                      {matches?.map((match, matchIndex) => (
                                        <BracketMatch
                                          key={match.id}
                                          match={match}
                                          videogame={bracket.videogame_slug}
                                          videogameTitle={getVideogameTitle(
                                            bracket.videogame_title
                                          )}
                                          tabIndex={
                                            matchIndex +
                                            100 * (dateIndex + 1) +
                                            1000 * (seedIndex + 1) +
                                            10000 * (columnIndex + 1)
                                          }
                                          display={matchDisplay}
                                          hoveredTeamId={hoveredTeamId}
                                          onOpponentHover={setHoveredTeamId}
                                        />
                                      ))}
                                    </div>
                                  ))}
                                </>
                              ) : (
                                <>
                                  {seed.matches.map((match, matchIndex) => {
                                    if (isPlaceholderMatch(match)) {
                                      // Invisible match keeping the slot of an opponent coming from elsewhere
                                      return (
                                        <div
                                          key={match.id}
                                          className={style.placeholderMatch}
                                          aria-hidden
                                        >
                                          <BracketMatch
                                            match={match}
                                            videogame={bracket.videogame_slug}
                                            videogameTitle={getVideogameTitle(
                                              bracket.videogame_title
                                            )}
                                            display={matchDisplay}
                                          />
                                        </div>
                                      );
                                    }

                                    const [prevLiaison, nextLiaison] = getMatchLiaisons(
                                      columnIndex - firstSeedColumnIndex[seedIndex],
                                      orderedColumns,
                                      columnIndex,
                                      match,
                                      matchIndex
                                    );

                                    const nextSeedMatchCount =
                                      orderedColumns[columnIndex + 1]?.seeds[seedIndex]?.matches
                                        .length ?? 0;
                                    const isSoloMatch =
                                      nextLiaison === 'top-straight' &&
                                      seed.matches.length - nextSeedMatchCount === 1;
                                    const nextSeedLength = isSoloMatch
                                      ? nextSeedMatchCount
                                      : undefined;
                                    const nextLiaisonIsTop = isSoloMatch && matchIndex === 0;

                                    return (
                                      <BracketMatch
                                        key={match.id}
                                        match={match}
                                        prevLiaison={prevLiaison}
                                        nextLiaison={nextLiaison}
                                        nextSeedLength={nextSeedLength}
                                        nextLiaisonIsTop={nextLiaisonIsTop}
                                        videogame={bracket.videogame_slug}
                                        videogameTitle={getVideogameTitle(bracket.videogame_title)}
                                        tabIndex={
                                          matchIndex +
                                          100 * (seedIndex + 1) +
                                          1000 * (columnIndex + 1)
                                        }
                                        display={matchDisplay}
                                        hoveredTeamId={hoveredTeamId}
                                        onOpponentHover={setHoveredTeamId}
                                      />
                                    );
                                  })}
                                </>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>

                    <div
                      className={classNames(
                        style.seedLiaisonContainer,
                        style.nextLiaisonContainer,
                        nextLiaison && style[nextLiaison]
                      )}
                    >
                      <div />
                    </div>
                  </div>
                );
              })}
              {thirdPlaceSeed !== undefined && columnIndex === numberOfColumns - 1 && (
                <div
                  className={classNames(style.thirdPlaceSeed, 'seed', {
                    [style.thirdPlaceSeedTop]: reseedingDisplay,
                    [style.withMinimap]: withMinimap
                  })}
                >
                  <div className={style.seedTitleContainer}>
                    {thirdPlaceSeed.title && (
                      <h3 className={style.seedTitle}>{thirdPlaceSeedTitle}</h3>
                    )}
                  </div>
                  <div className={style.seedContent}>
                    {thirdPlaceSeed.matches.map(match => (
                      // Match
                      <BracketMatch
                        key={match.id}
                        match={match}
                        videogame={bracket.videogame_slug}
                        videogameTitle={getVideogameTitle(bracket.videogame_title)}
                        tabIndex={1000 * (orderedColumns.length + 2)}
                        display={matchDisplay}
                        hoveredTeamId={hoveredTeamId}
                        onOpponentHover={setHoveredTeamId}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Minimap>
      {withStandingDisclaimerMessage && (
        <div className={style.standingDisclaimerMessageContainer}>
          <p className={style.standingDisclaimerMessage}>
            <InfoIcon className={style.infoIcon} />
            <span>{t('component-brackets-disclaimer')}</span>
          </p>
        </div>
      )}
    </>
  );
};

BracketWithSeeds.displayName = 'BracketWithSeeds';

export default BracketWithSeeds;
